import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { NavAccessService } from './core/services/ui/nav-access.service';
import {
  PERMISSION_CODES as P,
  requires,
  requiresAny,
} from './core/constants/permission-codes.constants';

export const routes: Routes = [
  {
    path: 'auth',
    loadComponent: () =>
      import('./layouts/auth-layout/auth-layout.component').then(
        (m) => m.AuthLayoutComponent,
      ),
    children: [
      {
        path: 'login',
        title: 'Iniciar sesión — Sorti Engage',
        loadComponent: () =>
          import('./features/auth/login/login.component').then(
            (m) => m.LoginComponent,
          ),
      },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
    ],
  },
  {
    path: '',
    loadComponent: () =>
      import('./layouts/main-layout/main-layout.component').then(
        (m) => m.MainLayoutComponent,
      ),
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        title: 'Dashboard — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.metricas.view),
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
          ),
      },
      {
        path: 'campaigns',
        title: 'Campañas — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.campanas.view),
        loadComponent: () =>
          import('./features/campaigns/campaigns.component').then(
            (m) => m.CampaignsComponent,
          ),
      },
      {
        path: 'tags',
        title: 'Tags — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.tags.view),
        loadComponent: () =>
          import('./features/tags/tags.component').then((m) => m.TagsComponent),
      },
      {
        path: 'landings',
        title: 'Landings — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.landings.view),
        loadComponent: () =>
          import('./features/landings/landings.component').then(
            (m) => m.LandingsComponent,
          ),
      },
      {
        path: 'predictors',
        title: 'Predictores — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.predictores.view),
        loadComponent: () =>
          import('./features/predictors/predictors.component').then(
            (m) => m.PredictorsComponent,
          ),
      },
      {
        path: 'registrations',
        title: 'Registros — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.registros.view),
        loadComponent: () =>
          import('./features/registrations/registrations.component').then(
            (m) => m.RegistrationsComponent,
          ),
      },
      {
        path: 'players',
        title: 'Jugadores — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.jugadores.view),
        loadComponent: () =>
          import('./features/players/players.component').then(
            (m) => m.PlayersComponent,
          ),
      },
      {
        path: 'submissions',
        title: 'Pronósticos — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.pronosticos.view),
        loadComponent: () =>
          import('./features/submissions/submissions.component').then(
            (m) => m.SubmissionsComponent,
          ),
      },
      {
        path: 'rewards',
        title: 'Recompensas — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.premios.view),
        loadComponent: () =>
          import('./features/rewards/rewards.component').then(
            (m) => m.RewardsComponent,
          ),
      },
      {
        path: 'users',
        title: 'Usuarios — Sorti Engage',
        canActivate: [permissionGuard],
        data: { permission: 'usuarios.view' },
        loadComponent: () =>
          import('./features/users/users.component').then(
            (m) => m.UsersComponent,
          ),
      },
      {
        path: 'audit-logs',
        title: 'Auditoría — Sorti Engage',
        canActivate: [permissionGuard],
        data: { permission: 'auditoria.view' },
        loadComponent: () =>
          import('./features/audit-logs/audit-logs.component').then(
            (m) => m.AuditLogsComponent,
          ),
      },
      {
        path: 'roles',
        title: 'Roles y Permisos — Sorti Engage',
        canActivate: [permissionGuard],
        data: { permission: 'roles.view' },
        loadComponent: () =>
          import('./features/roles/roles.component').then(
            (m) => m.RolesComponent,
          ),
      },
      {
        path: 'catalog',
        title: 'Catálogo — Sorti Engage',
        canActivate: [permissionGuard],
        data: requiresAny(P.catalogos.view, P.canjes.view),
        loadComponent: () =>
          import('./features/catalog/catalog.component').then(
            (m) => m.CatalogComponent,
          ),
      },
      {
        path: 'bonos',
        title: 'Bonos — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.bonos.view),
        loadComponent: () =>
          import('./features/bonos/bonos.component').then(
            (m) => m.BonosComponent,
          ),
      },
      {
        path: 'widget-config',
        title: 'Configuración del widget — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.widget.view),
        loadComponent: () =>
          import('./features/widget-config/widget-config.component').then(
            (m) => m.WidgetConfigComponent,
          ),
      },
      {
        path: 'settings',
        title: 'Configuraciones — Sorti Engage',
        canActivate: [permissionGuard],
        data: requires(P.sistema.view),
        loadComponent: () =>
          import('./features/settings/settings.component').then(
            (m) => m.SettingsComponent,
          ),
      },
      {
        path: 'profile',
        title: 'Mi perfil — Sorti Engage',
        loadComponent: () =>
          import('./features/profile/profile.component').then(
            (m) => m.ProfileComponent,
          ),
      },
      {
        path: '403',
        title: 'Acceso restringido — Sorti Engage',

        loadComponent: () =>
          import('./features/access-denied/access-denied.component').then(
            (m) => m.AccessDeniedComponent,
          ),
      },
      {
        path: '',
        redirectTo: () => inject(NavAccessService).firstAllowedPath(),
        pathMatch: 'full',
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
